import type { VariableADTvalue } from '@lighthouse/core'
import { PresetEnum } from '@lighthouse/core'
import type { ContainerLayoutContextValue, FlowLayoutNode } from '@lighthouse/shared'
import {
    combineBackgroundStyle,
    ContainerLayoutProvider,
    fieldConvertValue,
    getBackgroundStyle,
    getBorderStyle,
    getLayoutStyle,
    getPaddingStyle,
    getRadiusStyle,
    getShadowStyle,
    getVeinsStyle,
    NodeRender,
    useApplicationContext,
    useContainerLayoutContext,
    useFillPickerContext,
    useFlowLayoutContext
} from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import { TargetAndTransition } from 'motion'
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'

import { PanelProvider, useContainerBlockContext, useCustomViewBlockContext, usePanelContext } from '../../context'
import { animationLayoutIdRegistry } from '../../utils/AnimationLayoutIdRegistry'
import { AnimationDecorators } from '../AnimationDecorators'
import { getTransition } from '../AnimationDecorators/utils'
import { EmptyContainer } from '../EmptyContainer'

interface PanelLayoutProps extends React.ComponentPropsWithoutRef<'div'> {
    panelNode: FlowLayoutNode
    containerNode: FlowLayoutNode
    style?: React.CSSProperties
    disabled?: boolean
    containerStyle?: React.CSSProperties
    containerReference?: (node: HTMLDivElement | null) => void
    containerReferenceProps?: React.ComponentPropsWithoutRef<'div'>
    anchorId?: string
}

export const PanelLayout = forwardRef<HTMLDivElement, PanelLayoutProps>(
    (
        {
            style,
            containerStyle: containerStyleProp,
            containerReference,
            containerReferenceProps,
            containerNode,
            panelNode,
            disabled: propDisabled,
            children: propsChildren,
            anchorId,
            ...rest
        },
        ref
    ) => {
        const { appId = '', isBuilder } = useApplicationContext()
        const { parentPanelSmartAnimation } = useContainerBlockContext()
        const { level } = useContainerLayoutContext()
        const { disabled: globalDisabled, useParseVariableWithSubscription } = useFlowLayoutContext()
        const disabled = propDisabled ?? globalDisabled
        const { children, id } = panelNode
        const { id: containerId, data, block } = containerNode
        const containerBlockTitle = block?.title
        const { animation, layout, size, design } = data ?? {}
        const { padding } = layout ?? {}
        const { overflow } = size ?? {}
        const { panelChange } = animation ?? {}
        const { background, veins, border, shadow, blur, radius } = design ?? {}
        const { palettes } = useFillPickerContext()
        const { record } = useCustomViewBlockContext()
        const context = useMemo<ContainerLayoutContextValue>(
            () => ({ parentId: containerId, level: level + 1, ...data }),
            [containerId, data, level]
        )
        const { initial, animate, exit, transition } = useMemo(() => {
            if (!panelChange) {
                return {}
            }

            const { effect } = panelChange ?? {}
            if (!effect || effect.preset !== PresetEnum.fadeIn) {
                return {}
            }

            return {
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                exit: { opacity: 0 },
                transition: getTransition(effect.transition)
            }
        }, [panelChange])

        const [absolute, setAbsolute] = useState(false)

        const shouldManageAbsolute = panelChange?.effect?.preset === PresetEnum.fadeIn
        const currentPanelSmartAnimation = panelChange?.effect.preset === PresetEnum.smart

        useEffect(() => {
            return () => {
                if (currentPanelSmartAnimation) {
                    requestAnimationFrame(() => {
                        animationLayoutIdRegistry.reset(id)
                    })
                }
            }
        }, [id, currentPanelSmartAnimation])

        const { hasSmartAnimationParentPanel } = usePanelContext()
        const panelContextValue = useMemo(
            () => ({
                currentPanel: id,
                hasSmartAnimationParentPanel: hasSmartAnimationParentPanel || (currentPanelSmartAnimation ? id : '')
            }),
            [currentPanelSmartAnimation, hasSmartAnimationParentPanel, id]
        )

        const parseBackgroundImageVariable = useParseVariableWithSubscription(background?.image?.resource)
        const parseBackgroundImage = useCallback(
            (v: VariableADTvalue) => {
                const field = parseBackgroundImageVariable(v, { viewRecord: record })
                if (!field) {
                    return ''
                }

                return fieldConvertValue(field, { useFileUrl: true })
            },
            [parseBackgroundImageVariable, record]
        )
        const parseBackgroundColorVariable = useParseVariableWithSubscription(background?.variable)
        const parseBackgroundColor = useCallback(
            (v: VariableADTvalue) => {
                const field = parseBackgroundColorVariable(v, { viewRecord: record })
                if (!field) {
                    return ''
                }

                return fieldConvertValue(field)
            },
            [parseBackgroundColorVariable, record]
        )

        // 样式分层缓存，减少大对象重建，提升性能
        const baseContainerStyle: React.CSSProperties = useMemo(
            () => ({
                minWidth: 0,
                minHeight: 0,
                width: '100%',
                height: '100%',
                ...getPaddingStyle(padding, isBuilder),
                ...getLayoutStyle(layout, isBuilder),
                overflow,
                position: absolute ? 'absolute' : 'relative',
                inset: 0,
                WebkitBackdropFilter: blur ? `blur(${blur}px)` : undefined,
                backdropFilter: blur ? `blur(${blur}px)` : undefined,
                ...getRadiusStyle(radius),
                ...getBorderStyle(border, palettes),
                ...getShadowStyle(shadow, palettes)
            }),
            [absolute, blur, border, isBuilder, layout, overflow, padding, palettes, radius, shadow]
        )

        const variableBackgroundStyle = useMemo(() => {
            return combineBackgroundStyle([
                getBackgroundStyle({
                    appId,
                    value: background,
                    palettes,
                    parseVariableImage: parseBackgroundImage,
                    parseVariableColor: parseBackgroundColor
                }),
                getVeinsStyle(veins, palettes)
            ])
        }, [appId, background, palettes, parseBackgroundColor, parseBackgroundImage, veins])

        const containerStyle: React.CSSProperties = useMemo(
            () => ({
                ...baseContainerStyle,
                ...variableBackgroundStyle,
                ...containerStyleProp,
                ...style
            }),
            [baseContainerStyle, containerStyleProp, style, variableBackgroundStyle]
        )

        const shouldRenderAnimationDecorators = useMemo(() => {
            return !!animation || !!parentPanelSmartAnimation || !!initial || !!animate || !!exit || !!transition
        }, [animate, animation, exit, initial, parentPanelSmartAnimation, transition])

        const mergedContainerRef = containerReference ? mergeRefs([ref, containerReference]) : ref

        const panelContent = (
            <ContainerLayoutProvider value={context}>
                <PanelProvider value={panelContextValue}>
                    {children?.map(item => (
                        <NodeRender key={item.id} data={item} />
                    ))}
                    {!children?.length && !disabled && <EmptyContainer />}
                    {propsChildren}
                </PanelProvider>
            </ContainerLayoutProvider>
        )

        return shouldRenderAnimationDecorators ? (
            <AnimationDecorators
                ref={mergedContainerRef}
                {...containerReferenceProps}
                {...rest}
                id={anchorId}
                data-panel={id}
                data-block-id={containerId}
                blockId={containerId}
                blockName={containerBlockTitle}
                animation={animation}
                initial={initial}
                animate={animate}
                exit={exit}
                transition={transition}
                onAnimationStart={(definition: TargetAndTransition) => {
                    if (!shouldManageAbsolute) {
                        return
                    }
                    if (definition.opacity) {
                        setAbsolute(false)
                    }
                    if (!definition.opacity) {
                        setAbsolute(true)
                    }
                }}
                style={containerStyle}
                layout={!!parentPanelSmartAnimation}
            >
                {panelContent}
            </AnimationDecorators>
        ) : (
            <div
                ref={mergedContainerRef}
                id={anchorId}
                data-panel={id}
                data-block-id={containerId}
                style={containerStyle}
                {...containerReferenceProps}
                {...rest}
            >
                {panelContent}
            </div>
        )
    }
)
